import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Activity,
  TrendingUp,
  Wallet,
  Star,
  Calendar,
  Clock,
  Award,
  ChevronRight,
  Users,
  MapPin
} from "lucide-react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock upcoming sessions
const upcomingSessions = [
  { id: 1, patient: "Ahmad Rahman", time: "10:00 AM", type: "Home Visit", location: "Bangsar" },
  { id: 2, patient: "Sarah Lee", time: "2:00 PM", type: "Clinic", location: "PJ" },
  { id: 3, patient: "Kumar Rajan", time: "4:30 PM", type: "Teleconsult", location: "Online" },
];

// Mock earnings trend
const earningsTrend = [
  { day: "Mon", amount: 450 },
  { day: "Tue", amount: 380 },
  { day: "Wed", amount: 520 },
  { day: "Thu", amount: 480 },
  { day: "Fri", amount: 600 },
  { day: "Sat", amount: 350 },
  { day: "Sun", amount: 0 },
];

const tierConfig = {
  starter: { label: "Starter", color: "#94A3B8", nextTier: "Pro" },
  pro: { label: "Pro", color: "#60A5FA", nextTier: "Elite" },
  elite: { label: "Elite", color: "#F59E0B", nextTier: "Master" },
  master: { label: "Master", color: "#1E293B", nextTier: null },
};

export default function TherapistDashboard() {
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          axios.get(`${API}/therapist/stats`, { withCredentials: true }),
          axios.get(`${API}/therapist/profile`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setProfile(profileRes.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentTier = tierConfig[stats?.tier || "starter"];

  return (
    <div className="space-y-6" data-testid="therapist-dashboard">
      {/* Welcome Card */}
      <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-100">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={profile?.picture} />
                <AvatarFallback className="bg-primary text-white text-xl">
                  {profile?.name?.charAt(0) || "T"}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-heading font-bold text-2xl text-slate-900">
                  Welcome back, {profile?.name?.split(' ')[0]}!
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge 
                    style={{ backgroundColor: `${currentTier.color}20`, color: currentTier.color }}
                  >
                    {currentTier.label} Tier
                  </Badge>
                  <span className="text-sm text-slate-500">{profile?.role_type}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">Performance</p>
                <p className="font-heading font-bold text-2xl text-slate-900">{stats?.performance_score}%</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">Rating</p>
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="font-heading font-bold text-2xl text-slate-900">{stats?.rating?.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Today's Earnings</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  RM {stats?.today_earnings?.toFixed(0) || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">This Month</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  RM {stats?.monthly_earnings?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Sessions</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  {stats?.total_sessions || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Acceptance Rate</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  {stats?.acceptance_rate || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings Chart */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">This Week's Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={earningsTrend}>
                <defs>
                  <linearGradient id="therapistEarnings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F40d07" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#F40d07" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={(v) => `RM ${v}`} />
                <Tooltip 
                  formatter={(value) => [`RM ${value}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#F40d07" 
                  strokeWidth={2}
                  fill="url(#therapistEarnings)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Upcoming Sessions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Upcoming Sessions
            </CardTitle>
            <Button variant="ghost" size="sm" data-testid="view-all-sessions-btn">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <Clock className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{session.patient}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{session.type}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {session.location}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Badge variant="secondary">{session.time}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Tier Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${currentTier.color}20` }}
            >
              <Award className="w-10 h-10" style={{ color: currentTier.color }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-heading font-semibold text-lg" style={{ color: currentTier.color }}>
                    {currentTier.label}
                  </p>
                  <p className="text-sm text-slate-500">Current Tier</p>
                </div>
                {currentTier.nextTier && (
                  <div className="text-right">
                    <p className="font-heading font-semibold text-lg text-slate-400">
                      {currentTier.nextTier}
                    </p>
                    <p className="text-sm text-slate-500">Next Tier</p>
                  </div>
                )}
              </div>
              <Progress value={stats?.performance_score || 0} className="h-3" />
              <p className="text-sm text-slate-500 mt-2">
                {currentTier.nextTier 
                  ? `${100 - (stats?.performance_score || 0)}% more to reach ${currentTier.nextTier}`
                  : "You've reached the highest tier!"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="update-availability-btn">
          <Calendar className="w-6 h-6" />
          <span className="text-sm">Update Availability</span>
        </Button>
        <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="view-earnings-btn">
          <Wallet className="w-6 h-6" />
          <span className="text-sm">View Earnings</span>
        </Button>
        <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="training-btn">
          <Award className="w-6 h-6" />
          <span className="text-sm">Continue Training</span>
        </Button>
        <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="support-btn">
          <Activity className="w-6 h-6" />
          <span className="text-sm">Get Support</span>
        </Button>
      </div>
    </div>
  );
}
